import "./ReceiptTool.scss";
// REACT, ROUTER
import React, { useEffect, useRef, useState } from "react";

// COMPONENTS & FUNCTIONS
import ReceiptUnit from "../units/ReceiptUnit";
import useOutsideClick from "../../functions/useOutsideClick";
import { addAnimation } from "../../functions/animationHandler";
import { formatDate } from "../../functions/formatDate";
import { deleteAllOfTypeFromLocalStorage } from "../../functions/localStorageHandlers";

// NPM INSTALLS
import * as FaIcons from "react-icons/fa";

// INTERFACES
import { AppData, Receipt } from "../../interface/functionalityInterface";
import {
  ReceiptToolProps,
  ReceiptUnitProps,
} from "../../interface/propTypesInterface";

export default function ReceiptTool(props: ReceiptToolProps) {
  // ::::: CONSTANTS :::::
  const { receiptData, deleteAllReceipts, deleteReceipt } = props;
  const elemID = "receipt-tool";
  //  ::::: STATES :::::
  const [show, setshow] = useState(false);
  const [elem, setelem] = useState<HTMLElement | null>();

  // ::::: USEHOOKS :::::

  useEffect(() => {
    setelem(document.getElementById(elemID));
  }, []);

  useEffect(() => {
    if (show) {
      document.documentElement.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyPress);
    } else {
      document.documentElement.style.overflow = "scroll";
      window.removeEventListener("keydown", handleKeyPress);
    }
    return () => {
      document.documentElement.style.overflow = "scroll";
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [show]);

  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, () => {
    if (!show) return;
    handleExit();
  });

  // ::::: HANDLER FUNCTIONS :::::
  const handleDelete = (id: string) => {
    deleteReceipt(id);
  };

  const handleDeleteAll = () => {
    if (receiptData.length < 1) {
      // meTODO: No receipts to delete

      const receiptElem = document.getElementById("receipt-tool");
      if (receiptElem) addAnimation(receiptElem, "shakeHorError");
    } else {
      deleteAllReceipts();
      deleteAllOfTypeFromLocalStorage([AppData.RECEIPT]);
      // meTODO: all receipts deleted
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      setshow(false);
    }
  };

  const handlePrint = () => {
    if (receiptData.length < 1) {
      // meTODO: No receipts to print

      const receiptElem = document.getElementById("receipt-tool");
      if (receiptElem) addAnimation(receiptElem, "shakeHorError");
      return;
    }
    const formatReceipts = formatReceipt(receiptData);
    var textDoc = document.createElement("a");

    textDoc.href = "data:attachment/text," + encodeURI(formatReceipts);
    textDoc.target = "_blank";
    textDoc.download = "AbaCalc-Print-Data.txt";
    textDoc.click();

    // meTODO: Added receipt to textfile
  };

  const formatReceipt = (receipts: ReceiptUnitProps[]) => {
    const result = receipts.reduce((acc, receipt) => {
      const label = `--${receipt.label}--`;
      const mode = receipt.mode;
      const timestamp = `${formatDate(receipt.timestamp)}`;
      const output = receipt.output.reduce((acc, nV) => {
        return acc.concat(`${nV.name}: ${nV.value} ${nV.unit}\n`);
      }, "");
      return acc.concat(`${label}\n${mode}\n${timestamp}\n${output}\n`);
    }, "");
    return result;
  };

  const handleExit = () => {
    elem?.classList.remove("visible");
    setshow(false);
  };

  return (
    <div className="receipt" ref={ref}>
      <button
        id="show-receit-button"
        onClick={() => {
          setshow(true);
        }}
        className="rectangular-button translateY"
        type="button"
      >
        <FaIcons.FaReceipt />
        <span>show receipt</span>
      </button>
      <aside id={elemID} className={show ? "popup visible" : "popup"}>
        <button
          className="close-button"
          type="button"
          onClick={handleExit}
          style={{ position: "absolute" }}
          tabIndex={show ? 0 : -1}
        ></button>
        <h4 className="u-flex-center">Receipt List</h4>
        <div className="receipt__buttons">
          <button
            onClick={handlePrint}
            className="rectangular-button small"
            tabIndex={show ? 0 : -1}
          >
            <span style={{ marginRight: "1rem" }}>print receipt</span>
            <FaIcons.FaPrint />
          </button>
          <button
            onClick={handleDeleteAll}
            className="rectangular-button small"
            tabIndex={show ? 0 : -1}
          >
            <span style={{ marginRight: "1rem" }}>delete all</span>
            <FaIcons.FaTrash />
          </button>
        </div>

        <ul>
          {receiptData.map((receipt) => {
            return (
              <ReceiptUnit
                key={receipt.id}
                {...receipt}
                deleteCallback={handleDelete}
              />
            );
          })}
        </ul>
      </aside>
    </div>
  );
}
